<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex items-center gap-3">
                        <h1 class="stage-page-title">Position</h1>
                        <identifier-badge :value="position.id || '-'" title="Positions-ID"></identifier-badge>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" :to="positionsListPath">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                    </router-link>
                    <button type="button" class="btn-stage-icon-plain" :title="$t('Reload')" :aria-label="$t('Reload')" @click="getPosition">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill overflow-auto">
            <div class="relative">
                <loading :status="loading" background-color="transparent"/>

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                    <div class="space-y-6">
                        <div>
                            <app-panel title="Position" panel-class="overflow-visible">
                                    <div class="mb-5">
                                        <div class="flex items-center">
                                            <img
                                                v-if="customerCompanyImageUrl"
                                                :src="customerCompanyImageUrl"
                                                :alt="position.company && position.company.name ? position.company.name : 'Unternehmen'"
                                                class="h-8 w-8 rounded-sm mr-3 flex-shrink-0 object-cover"
                                                @error="handleCustomerCompanyImageError($event)"
                                            >
                                            <h2 class="text-xl font-semibold text-gray-900 leading-tight">{{ position.title || 'Position' }}</h2>
                                        </div>
                                    </div>
                                    <dl class="border border-bordergray-100 rounded bg-white divide-y divide-bordergray-100">
                                        <div class="px-5 py-3">
                                            <div class="w-full">
                                                <div class="flex items-center text-base text-gray-900">
                                                    <span>{{ getPositionProgressLabel() }}</span>
                                                    <app-tooltip
                                                        v-if="hasProcessStepsTooltip()"
                                                        class="ml-2"
                                                        width-class="w-max max-w-[22rem]"
                                                        placement="bottom"
                                                    >
                                                        <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                        <template #content>
                                                            <div class="px-3 py-2">
                                                                <div
                                                                    v-for="(processStep, processStepIndex) in getProcessStepsForTooltip()"
                                                                    :key="'position-customer-process-step-tooltip-' + position.id + '-' + processStep.id"
                                                                    class="position-process-step-tooltip-item"
                                                                >
                                                                    <span
                                                                        class="position-process-step-tooltip-dot"
                                                                        style="width: 10px; height: 10px;"
                                                                        :style="{ backgroundColor: getProcessStepDotColor(processStep, processStepIndex) }"
                                                                    ></span>
                                                                    <span
                                                                        :title="processStep.name"
                                                                        class="position-process-step-tooltip-text"
                                                                    >
                                                                        {{ processStep.name }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </app-tooltip>
                                                </div>
                                                <div class="mt-1 w-full">
                                                    <div class="h-1.5 bg-gray-300 rounded-full overflow-hidden">
                                                        <div
                                                            class="h-full rounded-full"
                                                            :style="{ width: getPositionProgressWidth() + '%', backgroundColor: getPositionProgressColor() }"
                                                        ></div>
                                                    </div>
                                                    <div class="mt-1 text-xs text-gray-500">
                                                        {{ getPositionProgressDone() }} / {{ getPositionProgressTotal() }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Positions-ID</dt>
                                            <dd class="text-sm text-gray-900 sm:text-right">{{ position.id || '-' }}</dd>
                                        </div>
                                        <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Zeithorizont</dt>
                                            <dd class="text-sm text-gray-900 sm:text-right">{{ formatDate(position.time_horizon) }}</dd>
                                        </div>
                                        <div v-if="hasCustomerTicketReference" class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Ticket-Referenz</dt>
                                            <dd class="sm:text-right">
                                                <router-link
                                                    :to="getCustomerTicketLink()"
                                                    class="text-sm text-gray-900 hover:text-gray-700 transition no-underline hover:no-underline"
                                                >
                                                    Ticket {{ position.customer_ticket_reference }} anzeigen
                                                </router-link>
                                            </dd>
                                        </div>
                                        <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Name</dt>
                                            <dd class="text-sm text-gray-900 sm:text-right">{{ position.author || '-' }}</dd>
                                        </div>
                                        <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Erstellt am</dt>
                                            <dd class="text-sm text-gray-900 sm:text-right">{{ formatShortDate(position.created_at) }}</dd>
                                        </div>
                                        <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                            <dt class="text-sm text-gray-500">Letztes Update</dt>
                                            <dd class="text-sm text-gray-900 sm:text-right">{{ $momentFormatDateTimeAgo(position.updated_at) }}</dd>
                                        </div>
                                    </dl>
                                    <div v-if="showNotesSection" class="text-xs uppercase tracking-wide text-gray-500 mt-6 mb-2">Anmerkungen</div>
                                    <div v-if="showNotesSection" class="border border-bordergray-100 rounded bg-white overflow-hidden divide-y divide-bordergray-100">
                                <div
                                    v-if="isEmployeeRole"
                                    class="bg-white overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        class="stage-header-surface w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none"
                                        @click="internalNoteOpen = !internalNoteOpen"
                                    >
                                        <span class="text-sm text-gray-500">Interne Anmerkung</span>
                                        <svg
                                            class="h-4 w-4 text-gray-400 transition-transform duration-150"
                                            :class="internalNoteOpen ? 'transform rotate-180' : ''"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </button>
                                    <div v-if="internalNoteOpen" class="px-5 pb-5 pt-4 border-t border-ultralight">
                                        <div v-if="internalNoteExists" class="text-sm text-gray-500 break-words" v-html="position.body"></div>
                                        <div v-else class="text-sm text-gray-500">Keine interne Anmerkung vorhanden.</div>
                                    </div>
                                </div>
                                <div
                                    v-if="!isEmployeeRole && positionNoteExists"
                                    class="bg-white overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        class="stage-header-surface w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none"
                                        @click="customerPositionDescriptionOpen = !customerPositionDescriptionOpen"
                                    >
                                        <span class="text-sm text-gray-500">Position</span>
                                        <svg
                                            class="h-4 w-4 text-gray-400 transition-transform duration-150"
                                            :class="customerPositionDescriptionOpen ? 'transform rotate-180' : ''"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </button>
                                    <div v-if="customerPositionDescriptionOpen" class="px-5 pb-5 pt-4 border-t border-ultralight">
                                        <div class="text-sm text-gray-900 mt-0 flex items-start">
                                            <img
                                                :src="getUserAvatar(position.user)"
                                                :alt="position.user && position.user.name ? position.user.name : 'Anfragender'"
                                                class="h-5 w-5 rounded-full mr-2 mt-0.5 flex-shrink-0"
                                                @error="handleAvatarImageError($event)"
                                            >
                                            <span class="whitespace-pre-line break-words">{{ position.customer_position_description }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="!isEmployeeRole && costApprovalNoteExists"
                                    class="bg-white overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        class="stage-header-surface w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none"
                                        @click="customerCostDescriptionOpen = !customerCostDescriptionOpen"
                                    >
                                        <span class="text-sm text-gray-500">Kostenfreigaben</span>
                                        <svg
                                            class="h-4 w-4 text-gray-400 transition-transform duration-150"
                                            :class="customerCostDescriptionOpen ? 'transform rotate-180' : ''"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </button>
                                    <div v-if="customerCostDescriptionOpen" class="px-5 pb-5 pt-4 border-t border-ultralight">
                                        <div class="text-sm text-gray-900 mt-0 flex items-start">
                                            <img
                                                :src="getUserAvatar(position.user)"
                                                :alt="position.user && position.user.name ? position.user.name : 'Anfragender'"
                                                class="h-5 w-5 rounded-full mr-2 mt-0.5 flex-shrink-0"
                                                @error="handleAvatarImageError($event)"
                                            >
                                            <span class="whitespace-pre-line break-words">{{ position.customer_cost_description }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="!isEmployeeRole && actionApprovalNoteExists"
                                    class="bg-white overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        class="stage-header-surface w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none"
                                        @click="customerActionDescriptionOpen = !customerActionDescriptionOpen"
                                    >
                                        <span class="text-sm text-gray-500">Aktionsfreigaben</span>
                                        <svg
                                            class="h-4 w-4 text-gray-400 transition-transform duration-150"
                                            :class="customerActionDescriptionOpen ? 'transform rotate-180' : ''"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </button>
                                    <div v-if="customerActionDescriptionOpen" class="px-5 pb-5 pt-4 border-t border-ultralight">
                                        <div class="text-sm text-gray-900 mt-0 flex items-start">
                                            <img
                                                :src="getUserAvatar(position.user)"
                                                :alt="position.user && position.user.name ? position.user.name : 'Anfragender'"
                                                class="h-5 w-5 rounded-full mr-2 mt-0.5 flex-shrink-0"
                                                @error="handleAvatarImageError($event)"
                                            >
                                            <span class="whitespace-pre-line break-words">{{ position.customer_action_description }}</span>
                                        </div>
                                    </div>
                                </div>
                                </div>
                            </app-panel>
                        </div>
                        <div>
                            <app-panel title="Prozessschritte" body-class="p-6">
                                    <div v-if="(position.process_steps || []).length > 0">
                                        <div
                                            v-for="(step, index) in position.process_steps"
                                            :key="'customer-position-step-' + step.id"
                                            class="flex items-stretch pb-3 last:pb-0"
                                        >
                                            <div class="relative w-7 mr-4 flex-shrink-0">
                                                <span
                                                    class="absolute top-5 left-1/2 z-10 -translate-x-1/2 inline-block h-3 w-3 rounded-full"
                                                    :style="getProcessStepTimelineDotStyle(step.color)"
                                                ></span>
                                                <span
                                                    v-if="index < (position.process_steps.length - 1)"
                                                    class="absolute left-1/2 z-0 -translate-x-1/2 w-px bg-bordergray-100"
                                                    style="top: 1.625rem; bottom: -2rem;"
                                                ></span>
                                            </div>
                                            <div
                                                class="flex-1 rounded border border-bordergray-100 bg-white overflow-hidden"
                                                :class="isProcessStepCompleted(step) ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-bordergray-100'"
                                            >
                                                <div class="w-full px-5 py-4 text-left">
                                                    <div v-if="processStepDateRange(step) || isProcessStepOverdue(step)" class="mb-3 inline-flex max-w-full items-center text-xs text-gray-500">
                                                        <span
                                                            v-if="processStepDateRange(step)"
                                                            class="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                                                        >
                                                            {{ processStepDateRange(step) }}
                                                        </span>
                                                        <svg
                                                            v-if="isProcessStepOverdue(step)"
                                                            class="h-5 w-5 text-red-500 ml-2 flex-shrink-0"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            stroke-width="1.8"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.72 3h16.92a2 2 0 0 0 1.72-3l-8.46-14.14a2 2 0 0 0-3.43 0z"/>
                                                            <path d="M12 9v4"/>
                                                            <circle cx="12" cy="17" r="1"/>
                                                        </svg>
                                                    </div>
                                                    <div class="min-w-0">
                                                        <div class="text-base font-semibold text-gray-900">{{ step.name }}</div>
                                                        <div
                                                            v-if="step.short_description"
                                                            class="text-sm text-gray-500 break-words"
                                                            style="hyphens: auto; -webkit-hyphens: auto;"
                                                            lang="de"
                                                        >
                                                            {{ step.short_description }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="isProcessStepCompleted(step)"
                                                    class="stage-footer-surface px-5 py-4 border-t border-ultralight"
                                                >
                                                    <div class="inline-flex items-center text-sm text-gray-700">
                                                        <span class="inline-block h-2.5 w-2.5 rounded-full mr-2 bg-green-500"></span>
                                                        Abgeschlossen am {{ formatProcessStepCompletedAt(step.completed_at) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-sm text-gray-500">
                                        Keine Prozessschritte vorhanden.
                                    </div>
                            </app-panel>
                        </div>
                    </div>

                    <div>
                        <app-panel v-model:open="costPanelOpen" title="Kostenfreigaben" collapsible body-class="p-6">
                            <div v-if="!canReadCostReleases" class="flex items-center text-sm text-gray-700">
                                <svg-vue class="h-4 w-4 text-red-600 mr-2 flex-shrink-0" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                <span>Fehlende Berechtigung</span>
                            </div>
                            <div v-else-if="(position.position_efforts || []).length > 0" class="space-y-6">
                                <div
                                    v-for="effort in position.position_efforts"
                                    :key="'customer-effort-card-' + effort.id"
                                    class="border border-bordergray-100 rounded bg-white overflow-hidden border-l-4"
                                    :class="approvalSideMarkClass(effort)"
                                >
                                    <div class="p-5">
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="text-base font-semibold text-gray-900">{{ effort.name }}</div>
                                            <div
                                                class="text-sm whitespace-nowrap"
                                                :class="effort.approval_status === 'cancelled' ? 'text-red-500 line-through' : 'text-gray-700'"
                                            >
                                                {{ formatHours(effort.customer_hours) }} Std.
                                            </div>
                                        </div>
                                        <div class="text-xs text-gray-500 mt-1">
                                            {{ approvalRequestText(effort.requested_at) }}
                                        </div>
                                        <div v-if="effort.description && effort.description.trim() !== ''" class="mt-2">
                                            <div class="text-sm text-gray-900">
                                                <span class="break-words">{{ effort.description }}</span>
                                            </div>
                                        </div>
                                        <dl class="mt-4 border border-bordergray-100 rounded bg-white divide-y divide-bordergray-100">
                                            <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                                <dt class="text-sm text-gray-500">Stundensatz</dt>
                                                <dd class="text-sm text-gray-900 sm:text-right">{{ formatCurrency(effort.customer_hourly_rate) }}</dd>
                                            </div>
                                            <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                                <dt class="text-sm text-gray-500">Quantifizierung</dt>
                                                <dd class="text-sm text-gray-900 sm:text-right">{{ formatQuantificationWithAmount(effort) }}</dd>
                                            </div>
                                            <div class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                                                <dt class="text-sm text-gray-500">Netto</dt>
                                                <dd class="text-sm text-gray-900 sm:text-right">{{ formatCurrency(effort.customer_total_amount) }}</dd>
                                            </div>
                                            <div class="px-5 py-3">
                                                <dd class="text-xs text-gray-500">{{ getVatHintText(effort) }}</dd>
                                            </div>
                                        </dl>
                                        <div v-if="effort.customer_note" class="text-sm text-gray-500 mt-2">
                                            <span class="break-words">{{ effort.customer_note }}</span>
                                        </div>
                                    </div>
                                    <div v-if="effort.approval_status === 'pending' && position.can_approve_cost_releases" class="px-5 pb-5">
                                        <div class="border border-bordergray-100 rounded bg-gray-100 overflow-hidden">
                                            <button
                                                type="button"
                                                class="w-full px-3 py-2 flex items-center justify-between text-left focus:outline-none cursor-pointer"
                                                @click="toggleCostApprovalNoteAccordion(effort.id)"
                                            >
                                                <span class="text-xs text-gray-600">Anmerkung (Optional):</span>
                                                <svg
                                                    class="h-3.5 w-3.5 text-gray-400 transition-transform duration-150"
                                                    :class="isCostApprovalNoteAccordionOpen(effort.id) ? 'transform rotate-180' : ''"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                                </svg>
                                            </button>
                                            <div v-if="isCostApprovalNoteAccordionOpen(effort.id)" class="px-3 pb-3">
                                                <textarea
                                                    :id="'customer_cost_approval_note_' + effort.id"
                                                    v-model="approvalNotes[effort.id]"
                                                    class="form-textarea mt-1 w-full h-24 border-ultralight rounded"
                                                    maxlength="1000"
                                                    rows="3"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="stage-footer-surface px-5 py-4 border-t border-ultralight flex items-center justify-between">
                                        <div
                                            v-if="effort.approval_status !== 'pending'"
                                            class="inline-flex items-center text-sm"
                                            :class="itemFooterTextClass(effort)"
                                        >
                                            <span class="inline-block h-2.5 w-2.5 rounded-full mr-2" :class="itemFooterDotClass(effort)"></span>
                                            {{ itemFooterStatusText(effort) }}
                                        </div>
                                        <div v-if="effort.approval_status === 'pending' && position.can_approve_cost_releases" class="flex justify-end ml-auto">
                                            <button
                                                :class="customerApprovalButtonClass(true)"
                                                :disabled="costApprovalLoading"
                                                type="button"
                                                @click="approveCost(effort.id)"
                                            >
                                                Freigeben
                                            </button>
                                        </div>
                                        <div v-if="effort.approval_status === 'pending' && !position.can_approve_cost_releases" class="ml-auto inline-flex items-center text-sm text-gray-900">
                                            <svg-vue class="h-4 w-4 text-red-600 mr-2 flex-shrink-0" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                            <span>Fehlende Freigabeberechtigung</span>
                                        </div>
                                        <svg
                                            v-if="effort.approval_status !== 'pending'"
                                            class="h-4 w-4 text-gray-500 ml-3 flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="1.8"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect x="5" y="11" width="14" height="10" rx="2" ry="2"/>
                                            <path d="M8 11V8a4 4 0 1 1 8 0v3"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div v-if="canReadCostReleases && (position.position_efforts || []).length === 0" class="flex items-center text-sm text-gray-700">
                                <svg-vue class="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" icon="font-awesome.regular.circle-info"></svg-vue>
                                <span>Keine Kostenfreigaben vorhanden</span>
                            </div>
                            <template #footer>
                                Kostenfreigaben sind bindend. Eine nachträgliche Stornierung kann nur durch einen Systemadministrator erfolgen.
                            </template>
                        </app-panel>
                    </div>

                    <div>
                        <app-panel v-model:open="actionPanelOpen" title="Aktionsfreigaben" collapsible body-class="p-6">
                            <div v-if="!canReadActionReleases" class="flex items-center text-sm text-gray-700">
                                <svg-vue class="h-4 w-4 text-red-600 mr-2 flex-shrink-0" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                <span>Fehlende Berechtigung</span>
                            </div>
                            <div v-else-if="(position.position_action_approvals || []).length > 0" class="space-y-6">
                                <div
                                    v-for="item in position.position_action_approvals"
                                    :key="'customer-action-card-' + item.id"
                                    class="border border-bordergray-100 rounded bg-white overflow-hidden border-l-4"
                                    :class="approvalSideMarkClass(item)"
                                >
                                    <div class="p-5">
                                        <div class="text-base font-semibold text-gray-900">{{ item.title }}</div>
                                        <div class="text-xs text-gray-500 mt-1">
                                            {{ approvalRequestText(item.requested_at) }}
                                        </div>
                                        <div v-if="item.description && item.description.trim() !== ''" class="mt-2">
                                            <div class="text-sm text-gray-900">
                                                <span class="break-words">{{ item.description }}</span>
                                            </div>
                                        </div>
                                        <div v-if="item.customer_note" class="text-sm text-gray-500 mt-2">
                                            <span class="break-words">{{ item.customer_note }}</span>
                                        </div>
                                    </div>
                                    <div v-if="item.approval_status === 'pending' && position.can_approve_action_releases" class="px-5 pb-5">
                                        <div class="border border-bordergray-100 rounded bg-gray-100 overflow-hidden">
                                            <button
                                                type="button"
                                                class="w-full px-3 py-2 flex items-center justify-between text-left focus:outline-none cursor-pointer"
                                                @click="toggleActionApprovalNoteAccordion(item.id)"
                                            >
                                                <span class="text-xs text-gray-600">Anmerkung (Optional):</span>
                                                <svg
                                                    class="h-3.5 w-3.5 text-gray-400 transition-transform duration-150"
                                                    :class="isActionApprovalNoteAccordionOpen(item.id) ? 'transform rotate-180' : ''"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                                </svg>
                                            </button>
                                            <div v-if="isActionApprovalNoteAccordionOpen(item.id)" class="px-3 pb-3">
                                                <textarea
                                                    :id="'customer_action_approval_note_' + item.id"
                                                    v-model="actionApprovalNotes[item.id]"
                                                    class="form-textarea mt-1 w-full h-24 border-ultralight rounded"
                                                    maxlength="1000"
                                                    rows="3"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="stage-footer-surface px-5 py-4 border-t border-ultralight flex items-center justify-between">
                                        <div
                                            v-if="item.approval_status !== 'pending'"
                                            class="inline-flex items-center text-sm"
                                            :class="itemFooterTextClass(item)"
                                        >
                                            <span class="inline-block h-2.5 w-2.5 rounded-full mr-2" :class="itemFooterDotClass(item)"></span>
                                            {{ itemFooterStatusText(item) }}
                                        </div>
                                        <div v-if="item.approval_status === 'pending' && position.can_approve_action_releases" class="flex justify-end ml-auto">
                                            <button
                                                :class="customerApprovalButtonClass(true)"
                                                :disabled="actionApprovalLoading"
                                                type="button"
                                                @click="approveAction(item.id)"
                                            >
                                                Freigeben
                                            </button>
                                        </div>
                                        <div v-if="item.approval_status === 'pending' && !position.can_approve_action_releases" class="ml-auto inline-flex items-center text-sm text-gray-900">
                                            <svg-vue class="h-4 w-4 text-red-600 mr-2 flex-shrink-0" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                            <span>Fehlende Freigabeberechtigung</span>
                                        </div>
                                        <svg
                                            v-if="item.approval_status !== 'pending'"
                                            class="h-4 w-4 text-gray-500 ml-3 flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="1.8"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect x="5" y="11" width="14" height="10" rx="2" ry="2"/>
                                            <path d="M8 11V8a4 4 0 1 1 8 0v3"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div v-if="canReadActionReleases && (position.position_action_approvals || []).length === 0" class="flex items-center text-sm text-gray-700">
                                <svg-vue class="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" icon="font-awesome.regular.circle-info"></svg-vue>
                                <span>Keine Aktionsfreigaben vorhanden</span>
                            </div>
                            <template #footer>
                                Aktionsfreigaben sind bindend. Eine nachträgliche Stornierung kann nur durch einen Systemadministrator erfolgen.
                            </template>
                        </app-panel>
                    </div>
                </div>

                <div v-if="position.attachments.length > 0" class="mt-4">
                    <app-panel
                        v-model:open="attachmentsPanelOpen"
                        title="Anhänge"
                        collapsible
                        body-class="p-6 flex flex-wrap items-start gap-3"
                    >
                        <attachment
                            v-for="attachment in position.attachments"
                            :key="'customer-position-attachment-' + attachment.id"
                            :details="attachment"
                            :remove-button="false"
                        />
                    </app-panel>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {resolveStoreAccess} from "@/utils/access-context";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {UI_COLORS} from '@/utils/ui-colors';

export default {
    name: "position-customer-manage",
    metaInfo() {
        return {
            title: "Position"
        }
    },
    data() {
        return {
            loading: true,
            position: {
                title: null,
                body: null,
                author: null,
                time_horizon: null,
                customer_ticket_reference: null,
                customer: null,
                requires_customer_cost_approval: false,
                can_read_cost_releases: false,
                can_approve_cost_releases: false,
                requires_customer_action_approval: false,
                can_read_action_releases: false,
                can_approve_action_releases: false,
                customer_position_description: null,
                customer_cost_description: null,
                customer_action_description: null,
                customer_cost_approval_status: 'pending',
                customer_cost_approved_at: null,
                process_steps: [],
                position_efforts: [],
                position_action_approvals: [],
                attachments: [],
                created_at: null,
                updated_at: null,
            },
            approvalNotes: {},
            costApprovalNoteAccordionOpen: {},
            costApprovalLoading: false,
            actionApprovalNotes: {},
            actionApprovalNoteAccordionOpen: {},
            actionApprovalLoading: false,
            internalNoteOpen: false,
            customerPositionDescriptionOpen: false,
            customerCostDescriptionOpen: false,
            customerActionDescriptionOpen: false,
            costPanelOpen: true,
            actionPanelOpen: true,
            attachmentsPanelOpen: true,
        };
    },
    computed: {
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        isEmployeeRole() {
            return !!this.accessContext.is_employee_like || !!this.accessContext.is_guest;
        },
        positionsListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/positions/list');
        },
        internalNoteExists() {
            return typeof this.position.body === 'string' && this.position.body.trim() !== '';
        },
        positionNoteExists() {
            return typeof this.position.customer_position_description === 'string'
                && this.position.customer_position_description.trim() !== '';
        },
        canReadCostReleases() {
            return this.normalizeBooleanFlag(this.position.can_read_cost_releases);
        },
        canReadActionReleases() {
            return this.normalizeBooleanFlag(this.position.can_read_action_releases);
        },
        costApprovalNoteExists() {
            return this.canReadCostReleases
                && typeof this.position.customer_cost_description === 'string'
                && this.position.customer_cost_description.trim() !== '';
        },
        actionApprovalNoteExists() {
            return this.canReadActionReleases
                && typeof this.position.customer_action_description === 'string'
                && this.position.customer_action_description.trim() !== '';
        },
        hasApprovalNotes() {
            return this.positionNoteExists || this.costApprovalNoteExists || this.actionApprovalNoteExists;
        },
        showNotesSection() {
            if (this.isEmployeeRole) {
                return this.internalNoteExists;
            }

            return this.hasApprovalNotes;
        },
        hasCustomerTicketReference() {
            return Number(this.position.customer_ticket_reference || 0) > 0;
        },
        customerCompanyImageUrl() {
            const company = this.position && this.position.company ? this.position.company : null;
            if (!company) {
                return null;
            }
            const value = typeof company.company_image === 'string' ? company.company_image.trim() : '';
            return value !== '' ? value : null;
        },
    },
    mounted() {
        this.getPosition();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        normalizeBooleanFlag(value) {
            return value === true || value === 1 || value === '1' || value === 'true';
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }
            return moment(value).format(window.app.app_date_format);
        },
        formatShortDate(value) {
            if (!value) {
                return '--.--.----';
            }
            const parsed = moment(value);
            if (!parsed.isValid()) {
                return '--.--.----';
            }
            return parsed.format('DD.MM.YYYY');
        },
        formatStepDate(value) {
            if (!value) {
                return '';
            }
            return moment(value).format('DD.MM.YYYY');
        },
        processStepDateRange(step) {
            if (!step) {
                return '';
            }
            const startDate = this.formatStepDate(step.start_date);
            const endDate = this.formatStepDate(step.end_date);
            if (startDate && endDate) {
                return `${startDate} bis ${endDate}`;
            }
            return startDate || endDate || '';
        },
        getPositionProgressTotal() {
            return Array.isArray(this.position.process_steps) ? this.position.process_steps.length : 0;
        },
        getPositionProgressDone() {
            const processSteps = this.getProcessStepsForTooltip();
            if (processSteps.length < 1) {
                return 0;
            }

            const completedCount = processSteps.filter((step) => this.isProcessStepCompleted(step)).length;
            const currentStepId = Number(this.position?.current_process_step?.id || 0);
            const currentStepIndex = currentStepId > 0
                ? processSteps.findIndex((step) => Number(step?.id || 0) === currentStepId)
                : -1;

            if (currentStepIndex > -1) {
                return Math.max(completedCount, currentStepIndex + 1);
            }

            return completedCount;
        },
        getPositionProgressWidth() {
            const total = this.getPositionProgressTotal();
            if (total < 1) {
                return 0;
            }
            const done = this.getPositionProgressDone();
            return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
        },
        getPositionProgressLabel() {
            const processSteps = this.getProcessStepsForTooltip();
            if (processSteps.length > 0) {
                const firstIncompleteStep = processSteps.find((step) => step && step.is_completed === false);
                if (firstIncompleteStep && firstIncompleteStep.name) {
                    return firstIncompleteStep.name;
                }

                const lastStep = processSteps[processSteps.length - 1];
                if (lastStep && lastStep.name) {
                    return lastStep.name;
                }
            }

            if (this.position && this.position.current_process_step && this.position.current_process_step.name) {
                return this.position.current_process_step.name;
            }
            if (this.position && this.position.process_step) {
                return this.position.process_step;
            }

            return processSteps.length > 0 ? 'Offen' : '-';
        },
        getPositionProgressColor() {
            if (this.position && this.position.current_process_step && this.position.current_process_step.color) {
                return this.position.current_process_step.color;
            }

            const processSteps = this.getProcessStepsForTooltip();
            if (processSteps.length < 1) {
                return UI_COLORS.text.default;
            }

            const completedSteps = processSteps.filter((step) => this.isProcessStepCompleted(step));
            if (completedSteps.length > 0) {
                const lastCompletedStep = completedSteps[completedSteps.length - 1];
                return lastCompletedStep.color || UI_COLORS.text.default;
            }

            return UI_COLORS.text.default;
        },
        getProcessStepsForTooltip() {
            if (!Array.isArray(this.position.process_steps)) {
                return [];
            }
            return this.position.process_steps
                .filter((step) => step && step.id && step.name)
                .sort((a, b) => Number(a.value || 0) - Number(b.value || 0));
        },
        hasProcessStepsTooltip() {
            return this.getProcessStepsForTooltip().length > 0;
        },
        getProcessStepDotColor(processStep, processStepIndex) {
            const currentStepId = Number(this.position?.current_process_step?.id || 0);
            if (currentStepId > 0 && Number(processStep?.id || 0) === currentStepId) {
                return processStep.color || UI_COLORS.text.subtle;
            }

            if (typeof processStep?.is_completed === 'boolean') {
                return processStep.is_completed ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
            }
            return processStepIndex < this.getPositionProgressDone() ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
        },
        getProcessStepTimelineDotStyle(color) {
            const baseColor = color || UI_COLORS.text.subtle;
            return {
                backgroundColor: baseColor,
                outline: `6px solid ${this.withOpacity(baseColor, 0.1)}`,
                outlineOffset: '0px',
            };
        },
        withOpacity(color, opacity) {
            const fallback = `rgba(156, 163, 175, ${opacity})`;
            if (!color || typeof color !== 'string') {
                return fallback;
            }

            const value = color.trim();
            if (value.startsWith('#')) {
                let hex = value.slice(1);
                if (hex.length === 3) {
                    hex = hex.split('').map((c) => c + c).join('');
                }
                if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) {
                    return fallback;
                }
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                return `rgba(${r}, ${g}, ${b}, ${opacity})`;
            }

            const rgbMatch = value.match(/^rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i);
            if (rgbMatch) {
                return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
            }

            const rgbaMatch = value.match(/^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*[\d.]+\s*\)$/i);
            if (rgbaMatch) {
                return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
            }

            return fallback;
        },
        getCustomerTicketLink() {
            return '/tickets/' + this.position.customer_ticket_reference;
        },
        isProcessStepCompleted(step) {
            if (!step) {
                return false;
            }
            if (typeof step.is_completed === 'boolean') {
                return step.is_completed;
            }
            if (step.completed_at) {
                return true;
            }
            return false;
        },
        isProcessStepOverdue(step) {
            if (!step || this.isProcessStepCompleted(step) || !step.end_date) {
                return false;
            }
            const endDate = moment(step.end_date, 'YYYY-MM-DD');
            if (!endDate.isValid()) {
                return false;
            }
            return endDate.endOf('day').isBefore(moment());
        },
        formatProcessStepCompletedAt(value) {
            if (!value) {
                return '--.--.---- um --:--';
            }
            const parsed = moment(value);
            if (!parsed.isValid()) {
                return '--.--.---- um --:--';
            }
            return parsed.format('DD.MM.YYYY [um] HH:mm');
        },
        formatApprovalRequestedAt(value) {
            if (!value) {
                return '--.--.---- um --:--';
            }
            const parsed = moment(value);
            if (!parsed.isValid()) {
                return '--.--.---- um --:--';
            }
            return parsed.format('DD.MM.YYYY [um] HH:mm');
        },
        approvalRequestText(value) {
            return this.formatApprovalRequestedAt(value);
        },
        formatApprovalStatusAt(value) {
            if (!value) {
                return '--.--.---- um --:--';
            }
            const parsed = moment(value);
            if (!parsed.isValid()) {
                return '--.--.---- um --:--';
            }
            return parsed.format('DD.MM.YYYY [um] HH:mm');
        },
        itemFooterDotClass(item) {
            if (item.approval_status === 'approved') {
                return 'bg-green-500';
            }
            if (item.approval_status === 'cancelled') {
                return 'bg-red-500';
            }
            return 'bg-gray-400';
        },
        itemFooterTextClass(item) {
            if (item && item.approval_status === 'cancelled') {
                return 'text-red-500';
            }
            return 'text-gray-700';
        },
        customerApprovalButtonClass(canApprove) {
            if (canApprove) {
                return 'btn btn-green shadow-sm rounded customer-approve-btn cursor-pointer disabled:cursor-not-allowed';
            }

            return 'btn btn-gray shadow-sm rounded opacity-60 cursor-not-allowed';
        },
        customerApprovalButtonTitle(canApprove) {
            return canApprove ? '' : 'Keine Freigabeberechtigung';
        },
        approvalSideMarkClass(item) {
            if (item.approval_status === 'approved') {
                return 'border-l-green-500';
            }
            if (item.approval_status === 'cancelled') {
                return 'border-l-red-500';
            }
            return 'border-l-bordergray-100';
        },
        getUserAvatar(user) {
            if (user && user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }
            if (user && user.gravatar) {
                return user.gravatar;
            }
            return 'https://www.gravatar.com/avatar/?d=mp';
        },
        handleAvatarImageError(event) {
            if (!event || !event.target) {
                return;
            }
            event.target.src = 'https://www.gravatar.com/avatar/?d=mp';
        },
        handleCustomerCompanyImageError(event) {
            if (!event || !event.target) {
                return;
            }
            event.target.style.display = 'none';
        },
        itemFooterStatusText(item) {
            if (item.approval_status === 'approved') {
                return `Freigabe erfolgt am ${this.formatApprovalStatusAt(item.approved_at)}`;
            }
            if (item.approval_status === 'cancelled') {
                return `Storniert am ${this.formatApprovalStatusAt(item.cancelled_at)}`;
            }
            return 'In Bearbeitung';
        },
        getPosition() {
            const self = this;
            self.loading = true;
            axios.get('api/positions/' + self.$route.params.id).then(function (response) {
                self.position = response.data;
                const notes = {};
                (response.data.position_efforts || []).forEach((effort) => {
                    notes[effort.id] = '';
                });
                self.approvalNotes = notes;
                const costNoteAccordionOpen = {};
                (response.data.position_efforts || []).forEach((effort) => {
                    costNoteAccordionOpen[effort.id] = false;
                });
                self.costApprovalNoteAccordionOpen = costNoteAccordionOpen;
                const actionNotes = {};
                (response.data.position_action_approvals || []).forEach((item) => {
                    actionNotes[item.id] = '';
                });
                self.actionApprovalNotes = actionNotes;
                const actionNoteAccordionOpen = {};
                (response.data.position_action_approvals || []).forEach((item) => {
                    actionNoteAccordionOpen[item.id] = false;
                });
                self.actionApprovalNoteAccordionOpen = actionNoteAccordionOpen;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/positions/list');
            });
        },
        approveCost(positionEffortId) {
            const self = this;
            if (self.costApprovalLoading) {
                return;
            }

            self.costApprovalLoading = true;
            axios.post('api/positions/' + self.$route.params.id + '/cost-approval', {
                position_effort_id: Number(positionEffortId),
                note: self.approvalNotes[positionEffortId] || null,
            }).then(function () {
                self.getPosition();
                self.costApprovalLoading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: 'Kostenfreigaben gespeichert.',
                    type: 'success'
                });
            }).catch(function () {
                self.costApprovalLoading = false;
            });
        },
        approveAction(positionActionApprovalId) {
            const self = this;
            if (self.actionApprovalLoading) {
                return;
            }

            self.actionApprovalLoading = true;
            axios.post('api/positions/' + self.$route.params.id + '/action-approval', {
                position_action_approval_id: Number(positionActionApprovalId),
                note: self.actionApprovalNotes[positionActionApprovalId] || null,
            }).then(function () {
                self.getPosition();
                self.actionApprovalLoading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: 'Aktionsfreigaben gespeichert.',
                    type: 'success'
                });
            }).catch(function () {
                self.actionApprovalLoading = false;
            });
        },
        formatHours(value) {
            const number = Number(value);
            if (!Number.isFinite(number)) {
                return '0,00';
            }
            return number.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        formatCurrency(value) {
            const number = Number(value);
            if (!Number.isFinite(number)) {
                return '0,00 €';
            }
            return `${number.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €`;
        },
        formatQuantification(effort) {
            const factor = Number(effort && effort.quantification_factor);
            const value = Number(effort && effort.quantification_value);
            if (!Number.isFinite(factor) || factor <= 0) {
                return 'Keine';
            }

            const normalizedValue = Number.isFinite(value) ? value : 0.25;
            return `QF ${Math.round(factor)} / QW ${normalizedValue.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        },
        formatQuantificationWithAmount(effort) {
            const factor = Number(effort && effort.quantification_factor);
            if (!Number.isFinite(factor) || factor <= 0) {
                return 'Keine';
            }

            const quantificationText = this.formatQuantification(effort);
            const netAmount = Number(effort && effort.customer_total_amount);
            const baseHours = Number(effort && effort.customer_hours);
            const hourlyRate = Number(effort && effort.customer_hourly_rate);

            if (!Number.isFinite(netAmount) || !Number.isFinite(baseHours) || !Number.isFinite(hourlyRate)) {
                return quantificationText;
            }

            const baseAmount = baseHours * hourlyRate;
            const quantificationAmount = netAmount - baseAmount;
            if (!Number.isFinite(quantificationAmount) || quantificationAmount <= 0) {
                return quantificationText;
            }

            return `${quantificationText} (${this.formatCurrency(quantificationAmount)})`;
        },
        getVatHintText(effort) {
            const vatRate = Number(effort && effort.vat_rate);
            const normalizedVatRate = Number.isFinite(vatRate) ? vatRate : 19;
            const formattedVatRate = Number.isInteger(normalizedVatRate)
                ? normalizedVatRate.toLocaleString('de-DE', {maximumFractionDigits: 0})
                : normalizedVatRate.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2});

            return `Zuzüglich ${formattedVatRate}% MwSt.`;
        },
        isCostApprovalNoteAccordionOpen(positionEffortId) {
            return !!this.costApprovalNoteAccordionOpen[positionEffortId];
        },
        toggleCostApprovalNoteAccordion(positionEffortId) {
            this.costApprovalNoteAccordionOpen = {
                ...this.costApprovalNoteAccordionOpen,
                [positionEffortId]: !this.isCostApprovalNoteAccordionOpen(positionEffortId),
            };
        },
        isActionApprovalNoteAccordionOpen(positionActionApprovalId) {
            return !!this.actionApprovalNoteAccordionOpen[positionActionApprovalId];
        },
        toggleActionApprovalNoteAccordion(positionActionApprovalId) {
            this.actionApprovalNoteAccordionOpen = {
                ...this.actionApprovalNoteAccordionOpen,
                [positionActionApprovalId]: !this.isActionApprovalNoteAccordionOpen(positionActionApprovalId),
            };
        },
    }
}
</script>

<style scoped>
.position-process-step-tooltip-item {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 0;
}

.position-process-step-tooltip-item:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 15px;
    bottom: -8px;
    width: 1px;
    background: var(--ui-border-default);
    z-index: 1;
}

.position-process-step-tooltip-dot {
    position: relative;
    z-index: 2;
    margin-right: 9px;
    flex-shrink: 0;
    border-radius: 9999px;
}

.position-process-step-tooltip-text {
    display: block;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 16px;
    color: var(--ui-text-secondary);
}

.customer-approve-btn:hover,
.customer-approve-btn:focus,
.customer-approve-btn:active {
    background-color: #16a34a !important;
    border-color: #16a34a !important;
    box-shadow: none !important;
    transform: none !important;
}
</style>
